import React from 'react';

export type PageLike = { title?: string; blocks?: any[] };

export type PageNavigationProps = {
  pages: PageLike[];
  currentIndex: number;
  onSelect: (index: number) => void;
  stylePreset?: 'classic' | 'minimal' | 'tabs';
  className?: string;
};

const PageNavigation: React.FC<PageNavigationProps> = ({ pages, currentIndex, onSelect, stylePreset = 'classic', className }) => {
  if (!pages || pages.length <= 1) return null;

  if (stylePreset === 'minimal') {
    return (
      <div className={"mt-10 "+(className||'')}> 
        <div className="flex items-center justify-between">
          <button
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            disabled={currentIndex <= 0}
            onClick={() => onSelect(Math.max(0, currentIndex - 1))}
            aria-label="Previous page"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Prev
          </button>
          <div className="flex-1 flex items-center justify-center flex-wrap gap-2">
            {pages.map((pg, i) => {
              const active = i === Math.min(currentIndex, pages.length-1);
              return (
                <button key={i}
                  className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-semibold transition ${active ? 'bg-gray-900 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  onClick={() => onSelect(i)} title={pg.title || `Page ${i+1}`}>{i+1}</button>
              );
            })}
          </div>
          <button
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            disabled={currentIndex >= pages.length - 1}
            onClick={() => onSelect(Math.min(pages.length - 1, currentIndex + 1))}
            aria-label="Next page"
          >
            Next
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      </div>
    );
  }

  if (stylePreset === 'tabs') {
    return (
      <div className={"mt-10 "+(className||'')}>
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex flex-wrap gap-2" aria-label="Tabs">
            {pages.map((pg, i) => {
              const active = i === Math.min(currentIndex, pages.length-1);
              return (
                <button key={i}
                  className={`px-3 py-2 text-sm font-medium border-b-2 ${active ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'}`}
                  onClick={() => onSelect(i)} title={pg.title || `Page ${i+1}`}
                >{pg.title || `Page ${i+1}`}</button>
              );
            })}
          </nav>
        </div>
      </div>
    );
  }

  // classic
  return (
    <div className={"mt-10 "+(className||'')}>
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-white via-white to-purple-50 shadow-sm">
        <div className="px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <button
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              disabled={currentIndex <= 0}
              onClick={() => onSelect(Math.max(0, currentIndex - 1))}
              aria-label="Previous page"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              Prev
            </button>
            <div className="flex-1 flex items-center justify-center flex-wrap gap-2">
              {pages.map((pg, i) => {
                const active = i === Math.min(currentIndex, pages.length-1);
                return (
                  <button
                    key={i}
                    className={`group inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition ${active ? 'border-purple-400 bg-purple-100 text-purple-800 shadow-sm' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}
                    onClick={() => onSelect(i)}
                    title={pg.title || `Page ${i+1}`}
                  >
                    <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${active ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 group-hover:bg-gray-200'}`}>{i+1}</span>
                    <span className="hidden sm:inline truncate max-w-[12ch]">{pg.title || `Page ${i+1}`}</span>
                  </button>
                );
              })}
            </div>
            <button
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              disabled={currentIndex >= pages.length - 1}
              onClick={() => onSelect(Math.min(pages.length - 1, currentIndex + 1))}
              aria-label="Next page"
            >
              Next
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
          <div className="mt-3">
            <div className="h-2 w-full rounded-full bg-gray-100">
              <div className="h-2 rounded-full bg-gradient-to-r from-purple-600 to-blue-500 transition-all" style={{ width: `${((Math.min(currentIndex, pages.length-1)+1)/pages.length)*100}%` }} />
            </div>
            <div className="mt-2 text-center text-xs text-gray-600">Page {Math.min(currentIndex, pages.length-1)+1} of {pages.length}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageNavigation;
